import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Save, ChevronLeft, ChevronRight, Download, DollarSign, Percent, Building2, HelpCircle, Info, X, FileSpreadsheet } from 'lucide-react';
import { UnsavedChangesDialog, useUnsavedChanges, useUnsavedBlocker, BlockerDialog } from './UnsavedChangesDialog';
import { exportFinancingToExcel } from './ExportExcelButton';
import { toast } from 'sonner';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList
} from 'recharts';

export const FinancingPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, updateProject } = useProject();
  const { showLabels, formatChartValue } = useChartPreferences();
  
  const [financing, setFinancing] = useState({
    total_investment: 0,
    equity_percentage: 100,
    debt_percentage: 0,
    equity_amount: 0,
    debt_amount: 0,
    cost_of_equity: 12,
    cost_of_debt: 10,
    tax_rate: 30,
    inflation_rate: 4,
    monthly_amortization: true,
    amortization_type: 'fixed',
    manual_amortization: {},
    wacc: 0
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('inputs');
  const [showWaccExplanation, setShowWaccExplanation] = useState(false);
  const [showFormulaPopup, setShowFormulaPopup] = useState(false);
  const [showDebtServiceExplanation, setShowDebtServiceExplanation] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [initialFinancing, setInitialFinancing] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await fetchProject(projectId);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject]);

  useEffect(() => {
    if (currentProject?.financing) {
      const totalInv = currentProject.investments?.reduce((sum, i) => sum + (i.amount || 0), 0) || 0;
      const newFinancing = {
        ...financing,
        ...currentProject.financing,
        total_investment: totalInv
      };
      setFinancing(newFinancing);
      setInitialFinancing(JSON.stringify(newFinancing));
    }
  }, [currentProject]);

  const calculateWACC = (data) => {
    const equityWeight = data.equity_percentage / 100;
    const debtWeight = data.debt_percentage / 100;
    const afterTaxDebt = data.cost_of_debt * (1 - data.tax_rate / 100);
    return (equityWeight * data.cost_of_equity) + (debtWeight * afterTaxDebt);
  };

  const handleEquityChange = (value) => {
    const equity = value[0];
    const debt = 100 - equity;
    const equityAmount = financing.total_investment * (equity / 100);
    const debtAmount = financing.total_investment * (debt / 100);
    
    const newFinancing = {
      ...financing,
      equity_percentage: equity,
      debt_percentage: debt,
      equity_amount: equityAmount,
      debt_amount: debtAmount
    };
    
    newFinancing.wacc = calculateWACC(newFinancing);
    setFinancing(newFinancing);
  };

  const handleRateChange = (field, value) => {
    const newFinancing = { ...financing, [field]: parseFloat(value) || 0 };
    newFinancing.wacc = calculateWACC(newFinancing);
    setFinancing(newFinancing);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProject(projectId, { financing });
      setInitialFinancing(JSON.stringify(financing));
      toast.success('Financiamiento guardado');
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Check for unsaved changes
  const hasUnsavedChanges = initialFinancing && JSON.stringify(financing) !== initialFinancing;

  const { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel } = useUnsavedBlocker(hasUnsavedChanges, handleSave);

  const goToNext = () => {
    if (!tryNavigate(`/app/project/${projectId}/sales-volume`)) {
      navigate(`/app/project/${projectId}/sales-volume`);
    }
  };

  const goToPrev = () => {
    if (!tryNavigate(`/app/project/${projectId}/investment`)) {
      navigate(`/app/project/${projectId}/investment`);
    }
  };

  // Chart data for pie
  const pieData = [
    { name: 'Capital', value: financing.equity_amount, color: '#3B82F6' },
    { name: 'Deuda', value: financing.debt_amount, color: '#94A3B8' }
  ].filter(d => d.value > 0);

  // Amortization schedule con intereses sobre saldo insoluto
  const years = currentProject?.specifications?.project_life_years || 5;
  
  const calculateAmortizationData = () => {
    const data = [];
    let balance = financing.debt_amount;
    const isManual = financing.amortization_type === 'manual';
    const annualPrincipal = !isManual && financing.debt_amount > 0 
      ? financing.debt_amount / years 
      : 0;
    
    for (let i = 0; i < years; i++) {
      const interest = balance * (financing.cost_of_debt / 100);
      let principal;
      
      if (isManual) {
        principal = financing.manual_amortization?.[String(i + 1)] || 0;
        // Last year: pay remaining balance
        if (i === years - 1) {
          principal = balance;
        }
        principal = Math.min(principal, balance);
      } else {
        principal = i === years - 1 ? balance : annualPrincipal;
      }
      
      data.push({
        year: `Ano ${i + 1}`,
        yearNum: i + 1,
        Principal: Math.round(principal),
        Intereses: Math.round(interest),
        'Saldo Insoluto': Math.round(balance)
      });
      
      balance = Math.max(0, balance - principal);
    }
    
    return data;
  };

  const amortizationData = calculateAmortizationData();
  
  // Calculate remaining debt for manual mode validation
  const manualTotalAssigned = Object.values(financing.manual_amortization || {}).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const manualRemaining = financing.debt_amount - manualTotalAssigned;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="financing-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 3 DE 10</span>
          </div>
          <h1 className="text-4xl font-bold">Financiamiento</h1>
          <p className="text-muted-foreground mt-1">
            Estructura de capital y costo promedio ponderado (WACC)
          </p>
        </div>
        <Button 
          variant="outline" 
          className="gap-2"
          onClick={() => exportFinancingToExcel(financing, currentProject?.name || 'Proyecto', currentProject?.specifications?.currency || 'MXN')}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Excel
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="inputs">INPUTS</TabsTrigger>
          <TabsTrigger value="summary">RESUMEN</TabsTrigger>
        </TabsList>

        <TabsContent value="inputs" className="mt-6 space-y-6">
          {/* Total Investment Display */}
          <Card className="bg-gradient-to-r from-card to-secondary/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inversión Total Requerida</p>
                  <p className="text-4xl font-bold">{formatCurrency(financing.total_investment, currentProject?.specifications?.currency || 'MXN')}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Suma de todos los conceptos de inversión
                  </p>
                </div>
                <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capital Structure */}
          <Card>
            <CardHeader>
              <CardTitle>Estructura de Capital</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label>Proporción Capital / Deuda</Label>
                  <div className="flex items-center gap-4">
                    <span className="text-primary font-semibold">
                      Capital: {financing.equity_percentage}%
                    </span>
                    <span className="text-muted-foreground font-semibold">
                      Deuda: {financing.debt_percentage}%
                    </span>
                  </div>
                </div>
                <Slider
                  value={[financing.equity_percentage]}
                  onValueChange={handleEquityChange}
                  min={0}
                  max={100}
                  step={5}
                  className="py-4"
                  data-testid="capital-slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>100% Deuda</span>
                  <span>100% Capital</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Monto Capital</p>
                  <p className="text-2xl font-bold text-primary">{formatCurrency(financing.equity_amount)}</p>
                </div>
                <div className="p-4 bg-secondary rounded-lg">
                  <p className="text-sm text-muted-foreground">Monto Deuda</p>
                  <p className="text-2xl font-bold">{formatCurrency(financing.debt_amount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rates */}
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Tasas y Costos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="cost-equity">Costo de Capital (%)</Label>
                  <Input
                    id="cost-equity"
                    type="number"
                    value={financing.cost_of_equity}
                    onChange={(e) => handleRateChange('cost_of_equity', e.target.value)}
                    className="mt-2"
                    data-testid="cost-equity-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cost-debt">Tasa de Deuda (%)</Label>
                  <Input
                    id="cost-debt"
                    type="number"
                    value={financing.cost_of_debt}
                    onChange={(e) => handleRateChange('cost_of_debt', e.target.value)}
                    className="mt-2"
                    data-testid="cost-debt-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tax-rate">Tasa Impositiva (%)</Label>
                  <Input
                    id="tax-rate"
                    type="number"
                    value={financing.tax_rate}
                    onChange={(e) => handleRateChange('tax_rate', e.target.value)}
                    className="mt-2"
                    data-testid="tax-rate-input"
                  />
                </div>
                <div>
                  <Label htmlFor="inflation">Tasa de Inflación (%)</Label>
                  <Input
                    id="inflation"
                    type="number"
                    value={financing.inflation_rate}
                    onChange={(e) => handleRateChange('inflation_rate', e.target.value)}
                    className="mt-2"
                    data-testid="inflation-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="amortization-card">
              <CardHeader>
                <CardTitle>Amortizacion del Credito</CardTitle>
                <p className="text-sm text-muted-foreground">
                  El crédito debe pagarse completo durante la vida del proyecto ({years} años)
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Amortization type selector */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className={cn(
                      "p-4 rounded-lg border-2 text-left transition-all",
                      financing.amortization_type === 'fixed' 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/50"
                    )}
                    onClick={() => setFinancing(prev => ({ ...prev, amortization_type: 'fixed' }))}
                    data-testid="amort-type-fixed"
                  >
                    <p className="font-semibold text-sm">Anual Fija</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Pagos iguales de capital cada año
                    </p>
                    {financing.debt_amount > 0 && financing.amortization_type === 'fixed' && (
                      <p className="text-lg font-bold text-primary mt-2">
                        {formatCurrency(financing.debt_amount / years)} /año
                      </p>
                    )}
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "p-4 rounded-lg border-2 text-left transition-all",
                      financing.amortization_type === 'manual' 
                        ? "border-primary bg-primary/10" 
                        : "border-border hover:border-primary/50"
                    )}
                    onClick={() => setFinancing(prev => ({ ...prev, amortization_type: 'manual' }))}
                    data-testid="amort-type-manual"
                  >
                    <p className="font-semibold text-sm">Override Manual</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Define el pago de capital por año
                    </p>
                  </button>
                </div>

                {/* Manual amortization inputs */}
                {financing.amortization_type === 'manual' && financing.debt_amount > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Deuda total a pagar:</span>
                      <span className="font-bold">{formatCurrency(financing.debt_amount)}</span>
                    </div>
                    
                    <div className="grid gap-2">
                      {Array.from({ length: years }, (_, i) => i + 1).map(year => {
                        const isLastYear = year === years;
                        let priorPaid = 0;
                        for (let y = 1; y < year; y++) {
                          priorPaid += parseFloat(financing.manual_amortization?.[String(y)] || 0);
                        }
                        const remainingForYear = financing.debt_amount - priorPaid;
                        
                        return (
                          <div key={year} className="flex items-center gap-3">
                            <span className="text-sm text-muted-foreground w-16 shrink-0">Anio {year}</span>
                            {isLastYear ? (
                              <div className="flex-1 p-2 bg-secondary/50 rounded text-sm">
                                <span className="text-muted-foreground">Saldo restante: </span>
                                <span className="font-bold">{formatCurrency(Math.max(0, remainingForYear))}</span>
                              </div>
                            ) : (
                              <Input
                                type="number"
                                min="0"
                                max={remainingForYear}
                                value={financing.manual_amortization?.[String(year)] || ''}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  setFinancing(prev => ({
                                    ...prev,
                                    manual_amortization: {
                                      ...prev.manual_amortization,
                                      [String(year)]: val
                                    }
                                  }));
                                }}
                                placeholder={`Pago capital año ${year}`}
                                className="flex-1"
                                data-testid={`manual-amort-year-${year}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className={cn(
                      "p-3 rounded-lg text-sm",
                      manualRemaining > 0 ? "bg-amber-500/10 text-amber-400" : "bg-emerald-500/10 text-emerald-400"
                    )}>
                      {manualRemaining > 0 
                        ? `Falta asignar ${formatCurrency(manualRemaining)} — se pagará automáticamente en el último año`
                        : `Deuda asignada correctamente`
                      }
                    </div>
                  </div>
                )}

                {/* Summary */}
                {financing.debt_amount > 0 && (
                  <div className="p-4 bg-secondary/50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Pago Total (Capital + Intereses)</p>
                    <p className="text-xl font-bold">
                      {formatCurrency(amortizationData.reduce((sum, d) => sum + d.Principal + d.Intereses, 0))}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Capital: {formatCurrency(financing.debt_amount)} + Intereses: {formatCurrency(amortizationData.reduce((sum, d) => sum + d.Intereses, 0))}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="summary" className="mt-6 space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-3 gap-6">
            <Card className="kpi-card">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-1">Deuda Total del Proyecto</p>
                <p className="text-3xl font-bold">{formatCurrency(financing.debt_amount)}</p>
                <p className="text-sm text-success mt-2">+{formatPercent(financing.debt_percentage)} del total</p>
              </CardContent>
            </Card>
            <Card className="kpi-card">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-1">Tasa de Interés Promedio</p>
                <p className="text-3xl font-bold">{formatPercent(financing.cost_of_debt)}</p>
                <p className="text-sm text-muted-foreground mt-2">Costo de la deuda</p>
              </CardContent>
            </Card>
            <Card className="kpi-card relative">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Servicio de Deuda Anual</p>
                    <p className="text-3xl font-bold">{formatCurrency(financing.debt_amount > 0 ? (financing.debt_amount / years) + (financing.debt_amount * financing.cost_of_debt / 100) : 0)}</p>
                    <p className="text-sm text-muted-foreground mt-2">Principal + Intereses (Año 1)</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={() => setShowDebtServiceExplanation(true)}
                  >
                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Debt Service Explanation Dialog */}
          <Dialog open={showDebtServiceExplanation} onOpenChange={setShowDebtServiceExplanation}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  ¿Qué es el Servicio de Deuda?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-muted-foreground">
                  El <strong className="text-foreground">Servicio de Deuda</strong> es el monto total que la empresa debe pagar anualmente para cubrir su deuda. Incluye:
                </p>
                <div className="space-y-2 bg-secondary/30 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Principal (Capital)</span>
                    <span className="font-medium">{formatCurrency(financing.debt_amount / years)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">+ Intereses (Año 1)</span>
                    <span className="font-medium">{formatCurrency(financing.debt_amount * financing.cost_of_debt / 100)}</span>
                  </div>
                  <div className="border-t border-border pt-2 flex items-center justify-between">
                    <span className="text-sm font-semibold">= Servicio de Deuda</span>
                    <span className="font-bold text-primary">{formatCurrency((financing.debt_amount / years) + (financing.debt_amount * financing.cost_of_debt / 100))}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Este indicador es crucial para evaluar la capacidad de la empresa de cumplir con sus obligaciones financieras.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowDebtServiceExplanation(false)}>Entendido</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-2 gap-6">
            {/* Capital Mix Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Mezcla de Capital</CardTitle>
                <p className="text-sm text-muted-foreground">Distribución Deuda vs Capital</p>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-8">
                  <ResponsiveContainer width={200} height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground uppercase">Total</p>
                    <p className="text-2xl font-bold">{formatCurrency(financing.total_investment)}</p>
                  </div>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary" />
                    <span className="text-sm">Capital ({financing.equity_percentage}%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-muted-foreground" />
                    <span className="text-sm">Deuda ({financing.debt_percentage}%)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WACC Billboard */}
            <Card className="bg-gradient-to-br from-secondary to-card">
              <CardContent className="p-8 flex flex-col items-center justify-center h-full">
                <span className="px-3 py-1 bg-success/20 text-success text-xs font-medium rounded-full mb-4">
                  CÁLCULO EN VIVO
                </span>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
                  Tasa del Proyecto Actual
                </p>
                <p className="text-7xl font-bold gradient-text mb-4">
                  {formatPercent(financing.wacc)}
                </p>
                <p className="text-lg font-semibold mb-2">WACC - Costo de Capital</p>
                <p className="text-sm text-muted-foreground text-center max-w-xs">
                  Esta tasa representa el retorno combinado esperado para todos los contribuidores de capital.
                </p>
                <div className="flex gap-3 mt-6">
                  <Button variant="outline" size="sm" onClick={() => setShowWaccExplanation(true)}>
                    <HelpCircle className="w-4 h-4 mr-2" />
                    ¿Qué es WACC?
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowFormulaPopup(true)}>
                    <Building2 className="w-4 h-4 mr-2" />
                    Ver Fórmula
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* WACC Explanation Dialog */}
          <Dialog open={showWaccExplanation} onOpenChange={setShowWaccExplanation}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-primary" />
                  ¿Qué es el WACC?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-muted-foreground">
                  El <strong className="text-foreground">WACC</strong> (Weighted Average Cost of Capital) o <strong className="text-foreground">Costo Promedio Ponderado de Capital</strong> es la tasa de rendimiento mínima que una empresa debe generar para satisfacer a todos sus proveedores de capital.
                </p>
                <div className="bg-secondary/30 p-4 rounded-lg space-y-3">
                  <h4 className="font-semibold">¿Por qué es importante?</h4>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li>• <strong className="text-foreground">Tasa de descuento:</strong> Se usa para calcular el Valor Presente Neto (VPN/NPV)</li>
                    <li>• <strong className="text-foreground">Benchmark:</strong> Si la TIR &gt; WACC, el proyecto genera valor</li>
                    <li>• <strong className="text-foreground">Decisiones:</strong> Ayuda a decidir si aceptar o rechazar proyectos</li>
                  </ul>
                </div>
                <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                  <p className="text-sm">
                    <strong>Tu WACC actual: </strong>
                    <span className="text-2xl font-bold text-primary">{formatPercent(financing.wacc)}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    El proyecto debe generar al menos esta tasa de retorno para ser viable.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowWaccExplanation(false)}>Entendido</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* WACC Formula Dialog */}
          <Dialog open={showFormulaPopup} onOpenChange={setShowFormulaPopup}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  Fórmula del WACC
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-secondary/50 p-6 rounded-lg text-center">
                  <p className="text-2xl font-mono font-bold">
                    WACC = (E/V × Re) + (D/V × Rd × (1-T))
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">E</span>
                      <span className="text-muted-foreground">= Capital propio</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">D</span>
                      <span className="text-muted-foreground">= Deuda total</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">V</span>
                      <span className="text-muted-foreground">= E + D (Capital total)</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">Re</span>
                      <span className="text-muted-foreground">= Costo del capital</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">Rd</span>
                      <span className="text-muted-foreground">= Costo de la deuda</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-primary">T</span>
                      <span className="text-muted-foreground">= Tasa impositiva</span>
                    </div>
                  </div>
                </div>
                <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                  <p className="font-semibold mb-2">Tu cálculo:</p>
                  <p className="font-mono text-sm">
                    WACC = ({financing.equity_percentage}% × {financing.cost_of_equity}%) + ({financing.debt_percentage}% × {financing.cost_of_debt}% × (1-{financing.tax_rate}%))
                  </p>
                  <p className="font-mono text-lg font-bold text-primary mt-2">
                    WACC = {formatPercent(financing.wacc)}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowFormulaPopup(false)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Amortization Schedule */}
          {financing.debt_amount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Pagos Anuales de Principal</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Calendario de amortización proyectado para los próximos {years} años
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={amortizationData} barCategoryGap="20%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                    <XAxis dataKey="year" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => formatChartValue(v)} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Legend />
                    <Bar dataKey="Principal" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                      {showLabels && (
                        <LabelList dataKey="Principal" position="top" formatter={(v) => formatChartValue(v)} fill="#3B82F6" fontSize={10} />
                      )}
                    </Bar>
                    <Bar dataKey="Intereses" fill="#94A3B8" radius={[4, 4, 0, 0]}>
                      {showLabels && (
                        <LabelList dataKey="Intereses" position="top" formatter={(v) => formatChartValue(v)} fill="#64748B" fontSize={10} />
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goToPrev}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button onClick={goToNext} data-testid="continue-btn">
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <BlockerDialog showDialog={showDialog} onSave={handleSaveAndProceed} onDiscard={handleDiscard} onCancel={handleCancel} saving={saving} />
    </div>
  );
};
